import { API_URL } from '../config';

class ChatService {
    constructor() {
        this.baseURL = `${API_URL}/api/chat`;
    }

    async getAuthHeaders() {
        const token = localStorage.getItem('access_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
        };
    }

    async handleResponse(response) {
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
        }
        return response.json();
    }

    // Get all conversations for the user
    async getConversations(page = 1, pageSize = 20) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(
                `${this.baseURL}/conversations/?page=${page}&page_size=${pageSize}`,
                {
                    method: 'GET',
                    headers
                }
            );
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error fetching conversations:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Create a new conversation
    async createConversation(name, selectedModelProvider = 'OpenAI', selectedModelName = 'gpt-4o-mini') {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(`${this.baseURL}/conversations/`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    name,
                    selected_model_provider: selectedModelProvider,
                    selected_model_name: selectedModelName
                })
            });
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error creating conversation:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Get conversation details
    async getConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(`${this.baseURL}/conversations/${conversationId}/`, {
                method: 'GET',
                headers
            });
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error fetching conversation:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Update conversation (name, model selection)
    async updateConversation(conversationId, updates) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(`${this.baseURL}/conversations/${conversationId}/`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(updates)
            });
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error updating conversation:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Delete conversation
    async deleteConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(`${this.baseURL}/conversations/${conversationId}/`, {
                method: 'DELETE',
                headers
            });
            
            if (response.ok) {
                return { success: true };
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            console.error('Error deleting conversation:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Get messages for a conversation
    async getMessages(conversationId, page = 1, pageSize = 50) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(
                `${this.baseURL}/conversations/${conversationId}/messages/?page=${page}&page_size=${pageSize}`,
                {
                    method: 'GET',
                    headers
                }
            );
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error fetching messages:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Send a message (creates user message and gets AI response)
    async sendMessage(conversationId, content, parentMessageId = null) {
        try {
            const headers = await this.getAuthHeaders();
            const payload = { content };
            if (parentMessageId) {
                payload.parent_message = parentMessageId;
            }

            const response = await fetch(`${this.baseURL}/conversations/${conversationId}/messages/`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error sending message:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Send a thread message
    async sendThreadMessage(conversationId, messageId, content) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await fetch(
                `${this.baseURL}/conversations/${conversationId}/messages/${messageId}/thread/`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ content })
                }
            );
            
            return {
                success: true,
                data: await this.handleResponse(response)
            };
        } catch (error) {
            console.error('Error sending thread message:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Transform API message format to frontend format
    transformMessage(apiMessage) {
        return {
            id: apiMessage.id,
            text: apiMessage.content,
            isUser: apiMessage.is_user,
            timestamp: new Date(apiMessage.created_at),
            model: apiMessage.model_used || 'Unknown',
            modelUsed: apiMessage.model_used,
            parentId: apiMessage.parent_message_id,
            threadCount: apiMessage.thread_count || 0,
            isError: false
        };
    }

    // Transform API conversation format to frontend format
    transformConversation(apiConversation) {
        return {
            id: apiConversation.id,
            name: apiConversation.name,
            createdAt: new Date(apiConversation.created_at),
            lastMessage: new Date(apiConversation.last_message),
            preview: apiConversation.preview || "No messages yet",
            messages: [], // Will be loaded separately
            threads: {} // Will be populated when messages are loaded
        };
    }

    // Format error messages for display
    formatErrorMessage(error) {
        if (typeof error === 'string') {
            return error;
        }
        return error.message || 'An unexpected error occurred';
    }
}

const chatService = new ChatService();
export default chatService; 